import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import {
  getOperationReportsListQueryKey,
  useOperationReportsCreate,
  useOperationReportsDestroy,
  useOperationReportsList,
  useOperationReportsPublishCreate,
  useOperationReportsPurchaseResultsRetrieve,
  useOperationReportsResultsDownloadRetrieve,
  useOperationReportsRetrieve,
  useOperationReportsSalesResultsRetrieve,
  useOperationReportsSummaryCreate,
  useOperationReportsUpdate,
} from '@shared/api/lib/operation-reports/operation-reports';
import { OperationReportsListParams } from '@shared/api/types';
import { useQueryClient } from '@tanstack/react-query';

export const useCreateOperationReport = () => {
  const queryClient = useQueryClient();
  return useOperationReportsCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getOperationReportsListQueryKey());
      },
    },
  });
};

export const usePublishOperationReport = () => {
  const queryClient = useQueryClient();
  return useOperationReportsPublishCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getOperationReportsListQueryKey());
      },
    },
  });
};

export const useGetOperationReports = (
  params: OperationReportsListParams,
  options?: {
    polling?: boolean;
    pollingInterval?: number;
  }
) => {
  const { polling, pollingInterval } = options || {};

  return useOperationReportsList(params, {
    request: useAxiosConfig(),
    query: {
      refetchInterval: polling && (pollingInterval || 1000),
      refetchIntervalInBackground: polling,
    },
  });
};

export const useGetPurchaseReport = (reportUuid: string) => {
  return useOperationReportsPurchaseResultsRetrieve(reportUuid, {
    request: useAxiosConfig(),
  });
};

export const useGetSalesReport = (reportUuid: string) => {
  return useOperationReportsSalesResultsRetrieve(reportUuid, {
    request: useAxiosConfig(),
  });
};

export const useGetOperationReport = (
  reportUuid: string,
  options?: {
    polling?: boolean;
    pollingInterval?: number;
    enabled?: boolean;
  }
) => {
  const { polling, pollingInterval } = options || {};
  return useOperationReportsRetrieve(reportUuid, {
    request: useAxiosConfig(),
    query: {
      enabled: !!reportUuid && (options?.enabled ?? true),
      refetchInterval: polling && (pollingInterval || 1000),
      refetchIntervalInBackground: polling,
    },
  });
};

export const useUpdateOperationReport = () => {
  const queryClient = useQueryClient();
  return useOperationReportsUpdate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getOperationReportsListQueryKey());
      },
    },
  });
};

export const useDeleteOperationReport = () => {
  const queryClient = useQueryClient();
  return useOperationReportsDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getOperationReportsListQueryKey());
      },
    },
  });
};

export const useDownloadOperationReportResults = (
  operationReportUuid: string,
  { enabled = false } = {}
) =>
  useOperationReportsResultsDownloadRetrieve(operationReportUuid, {
    request: useAxiosConfig(),
    query: {
      enabled: enabled,
      cacheTime: 0,
      staleTime: 0,
    },
  });

export const useCountReportRecords = () => {
  return useOperationReportsSummaryCreate({
    request: useAxiosConfig(),
  });
};
